import { AnyAction, Dispatch } from 'redux';
import { setPersonWithHandler } from '../../../../global/ducks/person-prc-api';
import { ApiStatuses } from '../../../../global/models/api-model';
import { DemographicAppKeys } from '../../../../global/models/demographics-model';
import { Person } from '../../../../global/models/person-model';
import { RelationshipsActionTypes, RelationshipsAppState, RelationshipsState, Spouse } from './relationships-model';

const InitialState: RelationshipsState = {
	spouses: [],
	loadingSpousePersons: 0,
	loadingSpouses: false,
	spousesFetched: false,
	childrenFetched: false,
	patchSpouseStatus: ApiStatuses.None,
	postSpouseStatus: ApiStatuses.None,
	widowedStatus: ApiStatuses.None,
	divorcedStatus: ApiStatuses.None,
};

const InitialAppState: RelationshipsAppState = {
	[DemographicAppKeys.myCPG]: InitialState,
	[DemographicAppKeys.arc]: InitialState,
};

// iterates over array, and updates person property if there is a client number match
const updateSpouseArray = (spouses: Spouse[], fetchedPerson: Person) => {
    return spouses.map((spouse: Spouse) => {
        if (spouse.person_guid !== fetchedPerson.person_guid) {
			return spouse;
		}

		return {
			...spouse,
			person: fetchedPerson,
		};
	});
};

const updateSpouseItem = (spouses: Spouse[], updatedSpouse: Spouse) => {
    return spouses.map((spouse: Spouse) => {
        if (spouse.person_guid !== updatedSpouse.person_guid) {
			return spouse;
		}

		return {
			...updatedSpouse,
			person: spouse.person,
		};
	});
};

export const relationshipsReducerByApp = (state = InitialAppState, action: AnyAction): RelationshipsAppState => {
	const { app } = action;

	switch (action.type) {
		case RelationshipsActionTypes.SET_SPOUSES:
			return {
				...state,
				[app]: {
					...state[app],
					spouses: action.payload.data,
					spousesFetched: true,
				},
			};
		case RelationshipsActionTypes.UPDATE_SPOUSE:
			return {
				...state,
				[app]: {
					...state[app],
					spouses: updateSpouseItem(state[app].spouses, action.payload),
				},
			};
		case RelationshipsActionTypes.UPDATE_SPOUSE_PERSON:
			return {
				...state,
				[app]: {
					...state[app],
					spouses: updateSpouseArray(state[app].spouses, action.payload),
				},
			};
		case RelationshipsActionTypes.SET_SPOUSE_PERSON:
			return {
				...state,
				[app]: {
					...state[app],
					spouses: updateSpouseArray(state[app].spouses, action.payload.data),
					loadingSpousePersons: state[app].loadingSpousePersons - 1,
				},
			};

		case RelationshipsActionTypes.ADD_LOADING_SPOUSE_PERSON:
			return {
				...state,
				[app]: {
					...state[app],
					loadingSpousePersons: state[app].loadingSpousePersons + 1,
				},
			};
		case RelationshipsActionTypes.PATCH_SPOUSE_STATUS:
			return {
				...state,
				[app]: {
					...state[app],
					patchSpouseStatus: action.payload,
				},
			};
		case RelationshipsActionTypes.POST_SPOUSE_STATUS:
			return {
				...state,
				[app]: {
					...state[app],
					postSpouseStatus: action.payload,
				},
			};
		case RelationshipsActionTypes.WIDOWED_STATUS:
			return {
				...state,
				[app]: {
					...state[app],
					widowedStatus: action.payload,
				},
			};
		case RelationshipsActionTypes.DIVORCED_STATUS:
			return {
				...state,
				[app]: {
					...state[app],
					divorcedStatus: action.payload,
				},
			};
		case RelationshipsActionTypes.SINGLE_STATUS:
			return {
				...state,
				[app]: {
					...state[app],
					singleStatus: action.payload,
				},
			};
		case RelationshipsActionTypes.LEGALLY_SEPARATED_STATUS:
			return {
				...state,
				[app]: {
					...state[app],
					legalSeperationStats: action.payload,
				},
			};

		case RelationshipsActionTypes.SET_SPOUSE_UNLOADED:
			return {
				...state,
				[app]: {
					...state[app],
					spousesFetched: false,
				},
			};

		case RelationshipsActionTypes.UNLOAD_RELATIONSHIPS_STATE:
			return {
				...state,
				[app]: {
					spouses: [],
					loadingSpousePersons: 0,
					loadingSpouses: false,
					spousesFetched: false,
					childrenFetched: false,
					patchSpouseStatus: ApiStatuses.None,
					postSpouseStatus: ApiStatuses.None,
					widowedStatus: ApiStatuses.None,
					divorcedStatus: ApiStatuses.None,
				},
			};
		default: return state;
	}
};

const addLoadingSpousePerson = (app: string) => {
	return {
		type: RelationshipsActionTypes.ADD_LOADING_SPOUSE_PERSON,
		app,
	};
};

export const addSpousePerson = (response: any, app: string) => {
	return {
		type: RelationshipsActionTypes.SET_SPOUSE_PERSON,
		payload: response,
		app,
	};
};

export const setSpouses = (response, app: string): AnyAction => {
	return {
		type: RelationshipsActionTypes.SET_SPOUSES,
		payload: response,
		app,
	};
};

export const setPatchSpouseStatus = (status: ApiStatuses, app: string) => {
	return {
		type: RelationshipsActionTypes.PATCH_SPOUSE_STATUS,
		payload: status,
		app,
	};
};

export const setPostSpouseStatus = (status: ApiStatuses, app: string) => {
	return {
		type: RelationshipsActionTypes.POST_SPOUSE_STATUS,
		payload: status,
		app,
	};
};

export const setWidowedStatus = (status: ApiStatuses, app: string) => {
	return {
		type: RelationshipsActionTypes.WIDOWED_STATUS,
		payload: status,
		app,
	};
};

export const setDivorcedStatus = (status: ApiStatuses, app: string) => {
	return {
		type: RelationshipsActionTypes.DIVORCED_STATUS,
		payload: status,
		app,
	};
};

export const setLegalSeparationStatus = (status: ApiStatuses, app: string) => {
	return {
		type: RelationshipsActionTypes.LEGALLY_SEPARATED_STATUS,
		payload: status,
		app,
	};
};

export const updateSpouse = (spouse: Spouse, app: string) => {
	return {
		type: RelationshipsActionTypes.UPDATE_SPOUSE,
		payload: spouse,
		app,
	};
};

export const updateSpousePerson = (person: Person, app: string) => {
	return {
		type: RelationshipsActionTypes.UPDATE_SPOUSE_PERSON,
		payload: person,
		app,
	};
};

export const dispatchSetSpouses = (dispatch: Dispatch<any>, app: string) => (response: any) => {
	const fetchedIds = new Set<string>();

	// fetch person data for each spouse
	const spouses: Spouse[] = response.data;
	for (let i = 0; i < spouses.length; i += 1) {
		const spouse = spouses[i];

        // only fetch one person per client number
        if (!fetchedIds.has(spouse.person_guid)) {
            fetchedIds.add(spouse.person_guid);

            dispatch(addLoadingSpousePerson(app));
            dispatch(setPersonWithHandler(spouses[i].person_guid, (personResponse) => dispatch(addSpousePerson(personResponse, app))));
		}
	}

	// sets the spouse array without the fetched person objects yet
	dispatch(setSpouses(response, app));
};

export const dispatchSetSpousesFailed = (dispatch: Dispatch<any>, app: string) => {
	dispatch(setSpouses({ data: [] }, app));
};

export const unloadRelationshipState = (app: string) => {
	return {
		type: RelationshipsActionTypes.UNLOAD_RELATIONSHIPS_STATE,
		app,
	};
};

export const setSingleStatus = (status: ApiStatuses, app: string) => {
	return {
		type: RelationshipsActionTypes.SINGLE_STATUS,
		payload: status,
		app,
	};
};

export const setSpouseUnloaded = (app: string) => {
	return {
		type: RelationshipsActionTypes.SET_SPOUSE_UNLOADED,
		app,
	};
};